import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import Navbar from "../components/Navbar";
import ImageUploadWithPosition from "../components/ImageUploadWithPosition";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { 
  Users, Calendar as CalendarIcon, MapPin, CreditCard, Trash2, 
  Edit, Crown, Store, DollarSign, Shield, Plus, Key, Settings, 
  Eye, EyeOff, Clock, Phone, Mail, Globe, Tag, Ticket, Guitar,
  UserPlus, CheckCircle, XCircle, AlertTriangle, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TAGS = ["concierto", "dj", "vermut", "afterwork", "promo", "comida", "afterparty"];
const MUSIC_GENRES = [
  "rock", "rock-clasico", "rock-alternativo", "rock-indie", "hard-rock",
  "punk", "punk-rock", "post-punk", "hardcore-punk",
  "metal", "heavy-metal", "thrash-metal", "death-metal", "black-metal",
  "power-metal", "doom-metal", "gothic-metal", "nu-metal", "metalcore",
  "grunge", "stoner", "post-rock", "emo"
];

const DAYS_OF_WEEK = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
const DAYS_LABELS = {
  lunes: "Lunes", martes: "Martes", miercoles: "Miércoles", 
  jueves: "Jueves", viernes: "Viernes", sabado: "Sábado", domingo: "Domingo"
};

export default function Admin() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bars, setBars] = useState([]);
  const [bands, setBands] = useState([]);
  const [pendingBars, setPendingBars] = useState([]);
  const [events, setEvents] = useState([]);
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [editBarDialog, setEditBarDialog] = useState(false);
  const [createBarDialog, setCreateBarDialog] = useState(false);
  const [editEventDialog, setEditEventDialog] = useState(false);
  const [createEventDialog, setCreateEventDialog] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [stripeDialog, setStripeDialog] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [createUserDialog, setCreateUserDialog] = useState(false);
  const [assignUserDialog, setAssignUserDialog] = useState(false);
  const [availableBarUsers, setAvailableBarUsers] = useState([]);
  
  // Forms
  const [selectedBar, setSelectedBar] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", user_type: "bar" });
  const [newBar, setNewBar] = useState({ 
    name: "", email: "", password: "", bar_name: "", description: "", 
    address: "", city_id: "", phone: "", website: "", contact: "", latitude: "", longitude: "",
    image_url: "", image_position: 50
  });
  const [newEvent, setNewEvent] = useState({ 
    bar_id: "", title: "", description: "", event_date: null, event_time: "20:00",
    end_time: "", image_url: "", image_position: 50, price: "", ticket_url: "", 
    ticket_info: "", tags: [], music_genres: []
  });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [stripeKey, setStripeKey] = useState("");
  const [stripeConfig, setStripeConfig] = useState(null);
  const [showStripeKey, setShowStripeKey] = useState(false);
  
  // Search and filters
  const [barSearch, setBarSearch] = useState("");
  const [barSortOrder, setBarSortOrder] = useState("name-asc");
  const [barCityFilter, setBarCityFilter] = useState("all");
  const [barLocationFilter, setBarLocationFilter] = useState("all"); // "all", "with-location", "without-location"
  const [eventBarFilter, setEventBarFilter] = useState("all");
  const [newCityName, setNewCityName] = useState("");
  const [creatingCity, setCreatingCity] = useState(false);
  
  // Multi-select for bars
  const [selectedBarIds, setSelectedBarIds] = useState([]);
  const [bulkEditDialog, setBulkEditDialog] = useState(false);
  const [bulkVenueType, setBulkVenueType] = useState("");
  const [bulkImageUrl, setBulkImageUrl] = useState("");
  
  // Venue types constant
  const VENUE_TYPES = [
    { id: "sala-conciertos", label: "Sala de Conciertos" },
    { id: "bar", label: "Bar" },
    { id: "cafe", label: "Café" },
    { id: "discoteca", label: "Discoteca" }
  ];
  
  // Plan features
  const [planFeatures, setPlanFeatures] = useState(null);
  const [featureLabels, setFeatureLabels] = useState({});
  const [savingFeatures, setSavingFeatures] = useState(false);
  
  // Band plan features
  const [bandPlanFeatures, setBandPlanFeatures] = useState(null);
  const [bandFeatureLabels, setBandFeatureLabels] = useState({});
  const [bandPlans, setBandPlans] = useState({});
  const [savingBandFeatures, setSavingBandFeatures] = useState(false);

  // Get fresh token for API calls - fallback to localStorage if context not yet updated
  const getAuthHeaders = () => {
    const currentToken = token || localStorage.getItem("token");
    return { Authorization: `Bearer ${currentToken}` };
  };

  useEffect(() => {
    // Wait for auth to be checked before redirecting
    if (authLoading) return;
    
    if (!user || user.user_type !== "admin") {
      navigate("/");
      return;
    }
    fetchAllData();
  }, [user, authLoading, navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [statsRes, barsRes, pendingBarsRes, eventsRes, paymentsRes, usersRes, citiesRes, stripeRes, featuresRes, bandFeaturesRes, bandsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/bars`, { headers }),
        axios.get(`${API}/admin/bars/pending`, { headers }),
        axios.get(`${API}/admin/events`, { headers }),
        axios.get(`${API}/admin/payments`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/cities`),
        axios.get(`${API}/admin/stripe-config`, { headers }),
        axios.get(`${API}/admin/plan-features`, { headers }),
        axios.get(`${API}/admin/band-plan-features`, { headers }),
        axios.get(`${API}/admin/bands`, { headers })
      ]);
      setStats(statsRes.data);
      setBars(barsRes.data);
      setBands(bandsRes.data);
      setPendingBars(pendingBarsRes.data);
      setEvents(eventsRes.data);
      setPayments(paymentsRes.data);
      setUsers(usersRes.data);
      setCities(citiesRes.data);
      setStripeConfig(stripeRes.data);
      setPlanFeatures(featuresRes.data.features);
      setFeatureLabels(featuresRes.data.labels);
      setBandPlanFeatures(bandFeaturesRes.data.features);
      setBandFeatureLabels(bandFeaturesRes.data.labels);
      setBandPlans(bandFeaturesRes.data.plans);
    } catch (error) {
      console.error("Error fetching admin data:", error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        toast.error("ACCESO DENEGADO - Inicia sesión de nuevo");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  // Filtered and sorted bars
  const filteredBars = bars
    .filter(bar => bar.name?.toLowerCase().includes(barSearch.toLowerCase()))
    .filter(bar => barCityFilter === "all" || bar.city_id === barCityFilter)
    .filter(bar => {
      if (barLocationFilter === "all") return true;
      const hasLocation = bar.latitude && bar.longitude;
      return barLocationFilter === "with-location" ? hasLocation : !hasLocation;
    })
    .sort((a, b) => {
      if (barSortOrder === "name-asc") return (a.name || "").localeCompare(b.name || "");
      if (barSortOrder === "name-desc") return (b.name || "").localeCompare(a.name || "");
      return 0;
    });
  
  // Stats for location filter
  const barsWithLocation = bars.filter(b => b.latitude && b.longitude).length;
  const barsWithoutLocation = bars.length - barsWithLocation;

  // Toggle bar selection for multi-select
  const toggleBarSelection = (barId) => {
    setSelectedBarIds(prev => 
      prev.includes(barId) 
        ? prev.filter(id => id !== barId)
        : [...prev, barId]
    );
  };

  // Select all visible bars
  const selectAllBars = () => {
    if (selectedBarIds.length === filteredBars.length) {
      setSelectedBarIds([]);
    } else {
      setSelectedBarIds(filteredBars.map(b => b.bar_id));
    }
  };

  // Bulk update bars
  const handleBulkUpdate = async () => {
    if (selectedBarIds.length === 0) {
      toast.error("SELECCIONA AL MENOS UN BAR");
      return;
    }
    
    const updates = {};
    if (bulkVenueType) updates.venue_type = bulkVenueType;
    if (bulkImageUrl) updates.image_url = bulkImageUrl;
    
    if (Object.keys(updates).length === 0) {
      toast.error("SELECCIONA AL MENOS UN CAMBIO");
      return;
    }
    
    try {
      await axios.post(`${API}/admin/bars/bulk-update`, {
        bar_ids: selectedBarIds,
        updates
      }, { headers: getAuthHeaders() });
      
      toast.success(`${selectedBarIds.length} BARES ACTUALIZADOS`);
      setBulkEditDialog(false);
      setSelectedBarIds([]);
      setBulkVenueType("");
      setBulkImageUrl("");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL ACTUALIZAR");
    }
  };

  // Filtered events
  const filteredEvents = eventBarFilter === "all" 
    ? events 
    : events.filter(e => e.bar_id === eventBarFilter);

  // Approve bar
  const handleApproveBar = async (barId) => {
    try {
      await axios.post(`${API}/admin/bars/${barId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success("BAR APROBADO");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL APROBAR");
    }
  };

  // Reject bar
  const handleRejectBar = async (barId, barName) => {
    if (!confirm(`¿Estás seguro de rechazar y eliminar el bar "${barName}"?`)) return;
    try {
      await axios.post(`${API}/admin/bars/${barId}/reject`, {}, { headers: getAuthHeaders() });
      toast.success("BAR RECHAZADO Y ELIMINADO");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL RECHAZAR");
    }
  };

  // Toggle bar visibility
  const handleToggleBarVisibility = async (barId, currentVisibility) => {
    try {
      const res = await axios.put(`${API}/admin/bars/${barId}/visibility`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message.toUpperCase());
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL CAMBIAR VISIBILIDAD");
    }
  };

  // Toggle band visibility
  const handleToggleBandVisibility = async (bandId, currentVisibility) => {
    try {
      const res = await axios.put(`${API}/admin/bands/${bandId}/visibility`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.message.toUpperCase());
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL CAMBIAR VISIBILIDAD");
    }
  };

  // Update plan feature
  const handleFeatureToggle = (plan, feature, value) => {
    setPlanFeatures(prev => ({
      ...prev,
      [plan]: {
        ...prev[plan],
        [feature]: value
      }
    }));
  };

  // Save plan features
  const handleSaveFeatures = async () => {
    setSavingFeatures(true);
    try {
      await axios.put(`${API}/admin/plan-features`, planFeatures, { headers: getAuthHeaders() });
      toast.success("CONFIGURACIÓN GUARDADA");
    } catch (error) {
      toast.error("ERROR AL GUARDAR");
    } finally {
      setSavingFeatures(false);
    }
  };

  // Update band plan feature
  const handleBandFeatureToggle = (plan, feature, value) => {
    setBandPlanFeatures(prev => ({
      ...prev,
      [plan]: {
        ...prev[plan],
        [feature]: value
      }
    }));
  };

  // Save band plan features
  const handleSaveBandFeatures = async () => {
    setSavingBandFeatures(true);
    try {
      await axios.put(`${API}/admin/band-plan-features`, bandPlanFeatures, { headers: getAuthHeaders() });
      toast.success("CONFIGURACIÓN DE BANDAS GUARDADA");
    } catch (error) {
      toast.error("ERROR AL GUARDAR");
    } finally {
      setSavingBandFeatures(false);
    }
  };

  // Create new city
  const handleCreateCity = async () => {
    if (!newCityName.trim()) {
      toast.error("ESCRIBE EL NOMBRE DE LA CIUDAD");
      return;
    }
    setCreatingCity(true);
    try {
      const res = await axios.post(`${API}/cities`, { name: newCityName.trim() }, { headers: getAuthHeaders() });
      toast.success("CIUDAD CREADA");
      setCities([...cities, res.data]);
      setNewCityName("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL CREAR CIUDAD");
    } finally {
      setCreatingCity(false);
    }
  };

  // Bar handlers
  const handleDeleteBar = async (barId, barName) => {
    if (!window.confirm(`¿Eliminar "${barName}" y todos sus eventos?`)) return;
    try {
      await axios.delete(`${API}/admin/bars/${barId}`, { headers: getAuthHeaders() });
      toast.success("BAR ELIMINADO");
      fetchAllData();
    } catch (error) {
      toast.error("ERROR AL ELIMINAR");
    }
  };

  // User handlers
  const handleEditUser = (user) => {
    setSelectedUser({ ...user, password: "" });
    setEditUserDialog(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;
    try {
      const updateData = { name: selectedUser.name, email: selectedUser.email, user_type: selectedUser.user_type };
      if (selectedUser.password) updateData.password = selectedUser.password;
      await axios.put(`${API}/admin/users/${selectedUser.user_id}`, updateData, { headers: getAuthHeaders() });
      toast.success("USUARIO ACTUALIZADO");
      setEditUserDialog(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL GUARDAR");
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`¿Eliminar al usuario "${userName}"? Si es propietario de un bar, también se eliminará el bar y sus eventos.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeaders() });
      toast.success("USUARIO ELIMINADO");
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL ELIMINAR");
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error("COMPLETA TODOS LOS CAMPOS");
      return;
    }
    try {
      const endpoint = newUser.user_type === "partner" 
        ? `${API}/admin/users/partner` 
        : `${API}/admin/users/bar`;
      await axios.post(endpoint, newUser, { headers: getAuthHeaders() });
      toast.success(`USUARIO ${newUser.user_type.toUpperCase()} CREADO`);
      setCreateUserDialog(false);
      setNewUser({ name: "", email: "", password: "", user_type: "bar" });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL CREAR");
    }
  };

  const handleOpenAssignUser = async (bar) => {
    setSelectedBar(bar);
    try {
      const res = await axios.get(`${API}/admin/users/bar/available`, { headers: getAuthHeaders() });
      setAvailableBarUsers(res.data);
      setAssignUserDialog(true);
    } catch (error) {
      toast.error("ERROR AL CARGAR USUARIOS");
    }
  };

  const handleAssignUser = async (userId) => {
    if (!selectedBar) return;
    try {
      await axios.put(`${API}/admin/bars/${selectedBar.bar_id}/assign-user?user_id=${userId}`, {}, { headers: getAuthHeaders() });
      toast.success("USUARIO ASIGNADO AL BAR");
      setAssignUserDialog(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL ASIGNAR");
    }
  };

  const handleUpdateSubscription = async (barId, newPlan) => {
    try {
      await axios.put(`${API}/admin/bars/${barId}/subscription`, { plan: newPlan }, { headers: getAuthHeaders() });
      toast.success(`SUSCRIPCIÓN ACTUALIZADA A ${newPlan.toUpperCase()}`);
      fetchAllData();
    } catch (error) {
      toast.error("ERROR AL ACTUALIZAR");
    }
  };

  const handleEditBar = (bar) => {
    setSelectedBar({ 
      ...bar,
      image_position: bar.image_position || 50,
      opening_hours: bar.opening_hours || []
    });
    setEditBarDialog(true);
  };

  const handleSaveBar = async () => {
    console.log("handleSaveBar called");
    console.log("selectedBar:", selectedBar);
    if (!selectedBar) {
      console.log("selectedBar is null, returning");
      return;
    }
    try {
      console.log("Sending PUT request with:", selectedBar);
      await axios.put(`${API}/admin/bars/${selectedBar.bar_id}`, selectedBar, { headers: getAuthHeaders() });
      toast.success("BAR ACTUALIZADO");
      setEditBarDialog(false);
      fetchAllData();
    } catch (error) {
      console.error("Error saving bar:", error);
      toast.error("ERROR AL GUARDAR");
    }
  };

  const handleCreateBar = async () => {
    if (!newBar.email || !newBar.password || !newBar.bar_name) {
      toast.error("COMPLETA TODOS LOS CAMPOS OBLIGATORIOS");
      return;
    }
    try {
      await axios.post(`${API}/admin/bars`, newBar, { headers: getAuthHeaders() });
      toast.success("BAR CREADO");
      setCreateBarDialog(false);
      setNewBar({ name: "", email: "", password: "", bar_name: "", description: "", address: "", city_id: "", phone: "", website: "", latitude: "", longitude: "", image_url: "", image_position: 50 });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL CREAR BAR");
    }
  };

  // Event handlers
  const handleDeleteEvent = async (eventId, eventTitle) => {
    if (!window.confirm(`¿Eliminar "${eventTitle}"?`)) return;
    try {
      await axios.delete(`${API}/admin/events/${eventId}`, { headers: getAuthHeaders() });
      toast.success("EVENTO ELIMINADO");
      fetchAllData();
    } catch (error) {
      toast.error("ERROR AL ELIMINAR");
    }
  };

  const handleEditEvent = (event) => {
    const eventDate = new Date(event.event_date);
    setSelectedEvent({
      ...event,
      event_date: eventDate,
      event_time: format(eventDate, "HH:mm"),
      end_time: event.end_date ? format(new Date(event.end_date), "HH:mm") : "",
      image_position: event.image_position || 50
    });
    setEditEventDialog(true);
  };

  const handleSaveEvent = async () => {
    if (!selectedEvent) return;
    try {
      const eventDate = new Date(selectedEvent.event_date);
      const [hours, minutes] = selectedEvent.event_time.split(":");
      eventDate.setHours(parseInt(hours), parseInt(minutes));
      
      let endDate = null;
      if (selectedEvent.end_time) {
        endDate = new Date(selectedEvent.event_date);
        const [endHours, endMinutes] = selectedEvent.end_time.split(":");
        endDate.setHours(parseInt(endHours), parseInt(endMinutes));
      }
      
      const eventData = {
        title: selectedEvent.title,
        description: selectedEvent.description,
        event_date: eventDate.toISOString(),
        end_date: endDate?.toISOString() || null,
        image_url: selectedEvent.image_url,
        price: selectedEvent.price,
        ticket_url: selectedEvent.ticket_url,
        ticket_info: selectedEvent.ticket_info,
        tags: selectedEvent.tags || [],
        music_genres: selectedEvent.music_genres || []
      };
      
      await axios.put(`${API}/admin/events/${selectedEvent.event_id}`, eventData, { headers: getAuthHeaders() });
      toast.success("EVENTO ACTUALIZADO");
      setEditEventDialog(false);
      fetchAllData();
    } catch (error) {
      toast.error("ERROR AL GUARDAR");
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.bar_id || !newEvent.title || !newEvent.event_date) {
      toast.error("COMPLETA TODOS LOS CAMPOS OBLIGATORIOS");
      return;
    }
    try {
      const eventDate = new Date(newEvent.event_date);
      const [hours, minutes] = newEvent.event_time.split(":");
      eventDate.setHours(parseInt(hours), parseInt(minutes));
      
      let endDate = null;
      if (newEvent.end_time) {
        endDate = new Date(newEvent.event_date);
        const [endHours, endMinutes] = newEvent.end_time.split(":");
        endDate.setHours(parseInt(endHours), parseInt(endMinutes));
      }
      
      const eventData = {
        title: newEvent.title,
        description: newEvent.description,
        event_date: eventDate.toISOString(),
        end_date: endDate?.toISOString() || null,
        image_url: newEvent.image_url,
        price: newEvent.price,
        ticket_url: newEvent.ticket_url,
        ticket_info: newEvent.ticket_info,
        tags: newEvent.tags || [],
        music_genres: newEvent.music_genres || []
      };
      
      await axios.post(`${API}/admin/events?bar_id=${newEvent.bar_id}`, eventData, { headers: getAuthHeaders() });
      toast.success("EVENTO CREADO");
      setCreateEventDialog(false);
      setNewEvent({ bar_id: "", title: "", description: "", event_date: null, event_time: "20:00", end_time: "", image_url: "", image_position: 50, price: "", ticket_url: "", ticket_info: "", tags: [], music_genres: [] });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL CREAR EVENTO");
    }
  };

  // Password handler
  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("LAS CONTRASEÑAS NO COINCIDEN");
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error("LA CONTRASEÑA DEBE TENER AL MENOS 6 CARACTERES");
      return;
    }
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { headers: getAuthHeaders() });
      toast.success("CONTRASEÑA ACTUALIZADA");
      setPasswordDialog(false);
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL CAMBIAR CONTRASEÑA");
    }
  };

  // Stripe config handler
  const handleSaveStripeConfig = async () => {
    if (!stripeKey) {
      toast.error("INTRODUCE LA API KEY DE STRIPE");
      return;
    }
    try {
      await axios.post(`${API}/admin/stripe-config`, { stripe_api_key: stripeKey }, { headers: getAuthHeaders() });
      toast.success("CONFIGURACIÓN DE STRIPE GUARDADA");
      setStripeDialog(false);
      setStripeKey("");
      fetchAllData();
    } catch (error) {
      toast.error("ERROR AL GUARDAR CONFIGURACIÓN");
    }
  };

  if (!user || user.user_type !== "admin") return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-[#111] w-1/3" />
            <div className="grid grid-cols-4 gap-4">
              {[1,2,3,4].map(i => <div key={i} className="h-32 bg-[#111]" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tighter">
              PANEL <span className="text-primary">ADMIN</span>
            </h1>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setPasswordDialog(true)} className="border-zinc-700 font-mono text-sm">
              <Key className="w-4 h-4 mr-2" />
              CONTRASEÑA
            </Button>
            <Button variant="outline" onClick={() => setStripeDialog(true)} className="border-zinc-700 font-mono text-sm">
              <Settings className="w-4 h-4 mr-2" />
              STRIPE
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-[#111] border border-zinc-800 p-4">
              <Users className="w-5 h-5 text-primary mb-2" />
              <p className="text-2xl font-black text-white">{stats.total_users}</p>
              <p className="font-mono text-xs text-zinc-500">USUARIOS</p>
            </div>
            <div className="bg-[#111] border border-zinc-800 p-4">
              <Store className="w-5 h-5 text-primary mb-2" />
              <p className="text-2xl font-black text-white">{stats.total_bars}</p>
              <p className="font-mono text-xs text-zinc-500">BARES</p>
            </div>
            <div className="bg-[#111] border border-zinc-800 p-4">
              <CalendarIcon className="w-5 h-5 text-primary mb-2" />
              <p className="text-2xl font-black text-white">{stats.total_events}</p>
              <p className="font-mono text-xs text-zinc-500">EVENTOS</p>
            </div>
            <div className="bg-[#111] border border-zinc-800 p-4">
              <Crown className="w-5 h-5 text-yellow-500 mb-2" />
              <p className="text-2xl font-black text-white">{stats.pro_subscriptions}</p>
              <p className="font-mono text-xs text-zinc-500">PRO</p>
            </div>
            <div className="bg-[#111] border border-zinc-800 p-4">
              <MapPin className="w-5 h-5 text-primary mb-2" />
              <p className="text-2xl font-black text-white">{stats.total_cities}</p>
              <p className="font-mono text-xs text-zinc-500">CIUDADES</p>
            </div>
            <div className="bg-[#111] border border-zinc-800 p-4">
              <DollarSign className="w-5 h-5 text-green-500 mb-2" />
              <p className="text-2xl font-black text-white">{stats.monthly_revenue}€</p>
              <p className="font-mono text-xs text-zinc-500">INGRESOS/MES</p>
            </div>
          </div>
        )}

        <Tabs defaultValue="bars" className="space-y-6">
          <TabsList className="bg-[#111] border border-zinc-800 rounded-none p-1 flex-wrap">
            {pendingBars.length > 0 && (
              <TabsTrigger value="pending" className="font-mono text-sm rounded-none data-[state=active]:bg-yellow-500 data-[state=active]:text-black relative">
                <AlertTriangle className="w-4 h-4 mr-1" />
                PENDIENTES
                <span className="absolute -top-1 -right-1 bg-yellow-500 text-black text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                  {pendingBars.length}
                </span>
              </TabsTrigger>
            )}
            <TabsTrigger value="bars" className="font-mono text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              BARES ({bars.length})
            </TabsTrigger>
            <TabsTrigger value="bands" className="font-mono text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              <Guitar className="w-4 h-4 mr-1" />
              BANDAS ({bands.length})
            </TabsTrigger>
            <TabsTrigger value="events" className="font-mono text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              EVENTOS ({events.length})
            </TabsTrigger>
            <TabsTrigger value="payments" className="font-mono text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              PAGOS ({payments.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="font-mono text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              USUARIOS ({users.length})
            </TabsTrigger>
            <TabsTrigger value="cities" className="font-mono text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              <MapPin className="w-4 h-4 mr-1" />
              CIUDADES ({cities.length})
            </TabsTrigger>
            <TabsTrigger value="features" className="font-mono text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              <Settings className="w-4 h-4 mr-1" />
              PLANES
            </TabsTrigger>
          </TabsList>

          {/* PENDING BARS TAB */}
          {pendingBars.length > 0 && (
            <TabsContent value="pending">
              <div className="bg-yellow-500/10 border border-yellow-500/50 p-4 mb-6">
                <h3 className="text-yellow-500 font-mono text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  BARES PENDIENTES DE APROBACIÓN ({pendingBars.length})
                </h3>
              </div>
              
              <div className="space-y-4">
                {pendingBars.map((bar) => (
                  <div key={bar.bar_id} className="bg-[#111] border border-zinc-800 p-6" data-testid={`pending-bar-${bar.bar_id}`}>
                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-2">{bar.name || "Sin nombre"}</h3>
                        <div className="space-y-1 text-sm">
                          <p className="text-zinc-400">
                            <Mail className="inline w-4 h-4 mr-2 text-zinc-600" />
                            {bar.owner_email || bar.email}
                          </p>
                          {bar.owner_name && (
                            <p className="text-zinc-400">
                              <Users className="inline w-4 h-4 mr-2 text-zinc-600" />
                              {bar.owner_name}
                            </p>
                          )}
                          <p className="text-zinc-500 text-xs">
                            <Clock className="inline w-3 h-3 mr-1" />
                            Registrado: {bar.created_at ? new Date(bar.created_at).toLocaleDateString("es-ES") : "N/A"}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 sm:flex-col">
                        <Button
                          onClick={() => handleApproveBar(bar.bar_id)}
                          className="bg-green-600 hover:bg-green-700 text-white font-mono"
                          data-testid={`approve-bar-${bar.bar_id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          APROBAR
                        </Button>
                        <Button
                          onClick={() => handleRejectBar(bar.bar_id, bar.name)}
                          variant="outline"
                          className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white font-mono"
                          data-testid={`reject-bar-${bar.bar_id}`}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          RECHAZAR
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          )}

          {/* BARS TAB */}
          <TabsContent value="bars">
            <div className="mb-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex gap-2 flex-wrap">
                <Button onClick={() => setCreateBarDialog(true)} className="btn-ruido bg-primary text-white font-mono">
                  <Plus className="w-4 h-4 mr-2" />
                  CREAR BAR
                </Button>
                {selectedBarIds.length > 0 && (
                  <Button onClick={() => setBulkEditDialog(true)} className="btn-ruido bg-blue-600 text-white font-mono">
                    <Edit className="w-4 h-4 mr-2" />
                    EDITAR {selectedBarIds.length} SELECCIONADOS
                  </Button>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Input
                  placeholder="Buscar por nombre..."
                  value={barSearch}
                  onChange={(e) => setBarSearch(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white font-mono text-sm w-full sm:w-48"
                />
                <Select value={barCityFilter} onValueChange={setBarCityFilter}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white font-mono text-sm w-full sm:w-40">
                    <SelectValue placeholder="Ciudad" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-zinc-800">
                    <SelectItem value="all" className="font-mono text-sm">TODAS CIUDADES</SelectItem>
                    {cities.map(city => (
                      <SelectItem key={city.city_id} value={city.city_id} className="font-mono text-sm">
                        {city.name.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={barLocationFilter} onValueChange={setBarLocationFilter}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white font-mono text-sm w-full sm:w-44">
                    <SelectValue placeholder="Ubicación" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-zinc-800">
                    <SelectItem value="all" className="font-mono text-sm">TODAS ({bars.length})</SelectItem>
                    <SelectItem value="with-location" className="font-mono text-sm">
                      <span className="flex items-center gap-2">
                        <MapPin className="w-3 h-3 text-green-500" /> CON MAPA ({barsWithLocation})
                      </span>
                    </SelectItem>
                    <SelectItem value="without-location" className="font-mono text-sm">
                      <span className="flex items-center gap-2">
                        <MapPin className="w-3 h-3 text-red-500" /> SIN MAPA ({barsWithoutLocation})
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Select value={barSortOrder} onValueChange={setBarSortOrder}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white font-mono text-sm w-full sm:w-40">
                    <SelectValue placeholder="Ordenar" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-zinc-800">
                    <SelectItem value="name-asc" className="font-mono text-sm">A-Z</SelectItem>
                    <SelectItem value="name-desc" className="font-mono text-sm">Z-A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="bg-[#111] border border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-900">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input 
                          type="checkbox" 
                          checked={selectedBarIds.length === filteredBars.length && filteredBars.length > 0}
                          onChange={selectAllBars}
                          className="rounded border-zinc-600"
                        />
                      </th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">NOMBRE</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">TIPO</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">CIUDAD</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">MAPA</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">PLAN</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">VISIBLE</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">ACCIONES</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {filteredBars.map((bar) => (
                      <tr key={bar.bar_id} className={`hover:bg-zinc-900/50 ${bar.is_visible === false ? 'opacity-50' : ''} ${selectedBarIds.includes(bar.bar_id) ? 'bg-blue-900/20' : ''}`}>
                        <td className="px-4 py-3">
                          <input 
                            type="checkbox" 
                            checked={selectedBarIds.includes(bar.bar_id)}
                            onChange={() => toggleBarSelection(bar.bar_id)}
                            className="rounded border-zinc-600"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {bar.image_url && (
                              <img src={bar.image_url} alt={bar.name} className="w-10 h-10 object-cover rounded" />
                            )}
                            <div>
                              <p className="font-bold text-white">{bar.name}</p>
                              <p className="font-mono text-xs text-zinc-500">{bar.address || "Sin dirección"}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-mono text-xs px-2 py-1 ${bar.venue_type ? 'bg-blue-500/20 text-blue-400' : 'bg-zinc-700 text-zinc-400'}`}>
                            {VENUE_TYPES.find(t => t.id === bar.venue_type)?.label?.toUpperCase() || "SIN TIPO"}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-zinc-400">
                          {cities.find(c => c.city_id === bar.city_id)?.name || "—"}
                        </td>
                        <td className="px-4 py-3">
                          {bar.latitude && bar.longitude ? (
                            <span className="bg-green-500/20 text-green-400 px-2 py-1 font-mono text-xs flex items-center gap-1 w-fit" title={`${bar.latitude.toFixed(4)}, ${bar.longitude.toFixed(4)}`}>
                              <MapPin className="w-3 h-3" /> SÍ
                            </span>
                          ) : (
                            <span className="bg-red-500/20 text-red-400 px-2 py-1 font-mono text-xs flex items-center gap-1 w-fit">
                              <MapPin className="w-3 h-3" /> NO
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Select value={bar.subscription_plan || "free"} onValueChange={(v) => handleUpdateSubscription(bar.bar_id, v)}>
                            <SelectTrigger className="w-24 bg-transparent border-zinc-700 rounded-none h-8 font-mono text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#111] border-zinc-800 rounded-none">
                              <SelectItem value="free" className="font-mono text-xs">FREE</SelectItem>
                              <SelectItem value="pro" className="font-mono text-xs">PRO</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleBarVisibility(bar.bar_id, bar.is_visible)}
                            className={`${bar.is_visible !== false ? 'text-green-500 hover:text-green-400' : 'text-zinc-600 hover:text-zinc-400'}`}
                            title={bar.is_visible !== false ? "Ocultar bar" : "Mostrar bar"}
                            data-testid={`bar-visibility-${bar.bar_id}`}
                          >
                            {bar.is_visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </Button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleOpenAssignUser(bar)} className="text-zinc-500 hover:text-blue-500" title="Asignar usuario">
                              <UserPlus className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleEditBar(bar)} className="text-zinc-500 hover:text-primary" title="Editar">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteBar(bar.bar_id, bar.name)} className="text-zinc-500 hover:text-red-500" title="Eliminar">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredBars.length === 0 && (
                <div className="p-8 text-center">
                  <Store className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">
                    {barSearch ? "NO SE ENCONTRARON BARES" : "NO HAY BARES REGISTRADOS"}
                  </p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* BANDS TAB */}
          <TabsContent value="bands">
            <div className="bg-[#111] border border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-900">
                    <tr>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">NOMBRE</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">PROPIETARIO</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">GÉNEROS</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">PLAN</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">VISIBLE</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {bands.map((band) => (
                      <tr key={band.band_id} className={`hover:bg-zinc-900/50 ${band.is_visible === false ? 'opacity-50' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {band.image_url && (
                              <img src={band.image_url} alt={band.name} className="w-10 h-10 object-cover rounded" />
                            )}
                            <div>
                              <p className="font-bold text-white">{band.name || "Sin nombre"}</p>
                              <p className="font-mono text-xs text-zinc-500">{band.city || "—"}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-zinc-300">{band.owner_name || "—"}</p>
                          <p className="font-mono text-xs text-zinc-500">{band.owner_email || "—"}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(band.music_genres || []).slice(0, 3).map((genre, i) => (
                              <span key={i} className="bg-zinc-800 text-zinc-400 text-[10px] px-2 py-0.5 font-mono">
                                {genre}
                              </span>
                            ))}
                            {(band.music_genres || []).length > 3 && (
                              <span className="text-zinc-600 text-[10px] font-mono">+{band.music_genres.length - 3}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 font-mono text-xs ${
                            band.subscription_plan === 'pro' 
                              ? 'bg-primary/20 text-primary' 
                              : 'bg-zinc-800 text-zinc-400'
                          }`}>
                            {band.subscription_plan === 'pro' ? 'STAGE' : 'GARAGE'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleBandVisibility(band.band_id, band.is_visible)}
                            className={`${band.is_visible !== false ? 'text-green-500 hover:text-green-400' : 'text-zinc-600 hover:text-zinc-400'}`}
                            title={band.is_visible !== false ? "Ocultar banda" : "Mostrar banda"}
                            data-testid={`band-visibility-${band.band_id}`}
                          >
                            {band.is_visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {bands.length === 0 && (
                <div className="p-8 text-center">
                  <Guitar className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">NO HAY BANDAS REGISTRADAS</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* EVENTS TAB */}
          <TabsContent value="events">
            <div className="mb-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex items-center gap-3">
                <Button onClick={() => setCreateEventDialog(true)} className="btn-ruido bg-primary text-white font-mono" disabled={bars.length === 0}>
                  <Plus className="w-4 h-4 mr-2" />
                  CREAR EVENTO
                </Button>
                {bars.length === 0 && <span className="text-zinc-500 font-mono text-sm">Crea un bar primero</span>}
              </div>
              <Select value={eventBarFilter} onValueChange={setEventBarFilter}>
                <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white font-mono text-sm w-full sm:w-48">
                  <SelectValue placeholder="Filtrar por bar" />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-zinc-800 max-h-60">
                  <SelectItem value="all" className="font-mono text-sm">TODOS LOS BARES</SelectItem>
                  {bars.map(bar => (
                    <SelectItem key={bar.bar_id} value={bar.bar_id} className="font-mono text-sm">
                      {bar.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-[#111] border border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-900">
                    <tr>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">EVENTO</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">BAR</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">FECHA</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">PRECIO</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">ACCIONES</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {filteredEvents.map((event) => (
                      <tr key={event.event_id} className="hover:bg-zinc-900/50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {event.image_url && (
                              <img src={event.image_url} alt={event.title} className="w-10 h-10 object-cover rounded" />
                            )}
                            <div>
                              <p className="font-bold text-white">{event.title}</p>
                              <div className="flex gap-1 mt-1">
                                {event.tags?.slice(0, 2).map(tag => (
                                  <span key={tag} className="bg-primary/20 text-primary text-xs font-mono px-1">{tag}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-zinc-400">{event.bar_name}</td>
                        <td className="px-4 py-3 font-mono text-sm text-zinc-400">
                          {format(new Date(event.event_date), "d MMM yyyy HH:mm", { locale: es })}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-zinc-300">{event.price || "Gratis"}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleEditEvent(event)} className="text-zinc-500 hover:text-primary">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteEvent(event.event_id, event.title)} className="text-zinc-500 hover:text-red-500">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {events.length === 0 && (
                <div className="p-8 text-center">
                  <CalendarIcon className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">NO HAY EVENTOS</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* PAYMENTS TAB */}
          <TabsContent value="payments">
            <div className="bg-[#111] border border-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-900">
                    <tr>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">FECHA</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">BAR</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">CANTIDAD</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">ESTADO</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {payments.map((tx, idx) => (
                      <tr key={idx} className="hover:bg-zinc-900/50">
                        <td className="px-4 py-3 font-mono text-sm text-zinc-400">
                          {tx.created_at ? format(new Date(tx.created_at), "d MMM yyyy HH:mm", { locale: es }) : "—"}
                        </td>
                        <td className="px-4 py-3 text-white">{tx.bar_name}</td>
                        <td className="px-4 py-3 font-mono text-sm text-green-400">{tx.amount ? `${tx.amount}€` : "50€"}</td>
                        <td className="px-4 py-3">
                          <span className={`font-mono text-xs px-2 py-1 ${tx.status === "paid" || tx.status === "complete" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                            {tx.status?.toUpperCase() || "PENDING"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {payments.length === 0 && (
                <div className="p-8 text-center">
                  <CreditCard className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">NO HAY PAGOS REGISTRADOS</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users">
            <div className="bg-[#111] border border-zinc-800 overflow-hidden">
              <div className="p-4 border-b border-zinc-800 flex justify-between items-center">
                <h3 className="text-lg font-bold text-white">USUARIOS</h3>
                <Button onClick={() => setCreateUserDialog(true)} className="btn-ruido bg-primary text-white font-mono text-xs">
                  + CREAR USUARIO BAR
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-900">
                    <tr>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">NOMBRE</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">EMAIL</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">TIPO</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">REGISTRO</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">ACCIONES</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {users.map((u) => (
                      <tr key={u.user_id} className="hover:bg-zinc-900/50">
                        <td className="px-4 py-3 text-white">{u.name}</td>
                        <td className="px-4 py-3 font-mono text-sm text-zinc-400">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className={`font-mono text-xs px-2 py-1 ${u.user_type === "admin" ? "bg-primary/20 text-primary" : u.user_type === "bar" ? "bg-yellow-500/20 text-yellow-400" : u.user_type === "partner" ? "bg-blue-500/20 text-blue-400" : "bg-zinc-700 text-zinc-300"}`}>
                            {u.user_type?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                          {u.created_at ? format(new Date(u.created_at), "d MMM yyyy", { locale: es }) : "—"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button onClick={() => handleEditUser(u)} className="text-zinc-500 hover:text-primary" title="Editar">
                              <Edit className="w-4 h-4" />
                            </button>
                            {u.user_type !== "admin" && (
                              <button onClick={() => handleDeleteUser(u.user_id, u.name)} className="text-zinc-500 hover:text-red-500" title="Eliminar">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* CITIES TAB */}
          <TabsContent value="cities">
            <div className="bg-[#111] border border-zinc-800 p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black text-white">GESTIÓN DE CIUDADES</h3>
                <div className="flex gap-2">
                  <Input 
                    value={newCityName} 
                    onChange={(e) => setNewCityName(e.target.value)} 
                    className="input-ruido w-48" 
                    placeholder="Nueva ciudad..."
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateCity()}
                  />
                  <Button 
                    onClick={handleCreateCity} 
                    disabled={creatingCity || !newCityName.trim()}
                    className="bg-primary hover:bg-red-700 text-white font-mono"
                  >
                    {creatingCity ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                    AÑADIR
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-900">
                    <tr>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">NOMBRE</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">PAÍS</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">COORDENADAS</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">BARES</th>
                      <th className="px-4 py-3 text-left font-mono text-xs text-zinc-500">ACCIONES</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {cities.map((city) => {
                      const cityBars = bars.filter(b => b.city_id === city.city_id);
                      return (
                        <tr key={city.city_id} className="hover:bg-zinc-900/50">
                          <td className="px-4 py-3">
                            <p className="font-bold text-white">{city.name}</p>
                            <p className="font-mono text-xs text-zinc-600">{city.city_id}</p>
                          </td>
                          <td className="px-4 py-3 font-mono text-sm text-zinc-400">
                            {city.country || "España"}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                            {city.latitude && city.longitude 
                              ? `${city.latitude.toFixed(2)}, ${city.longitude.toFixed(2)}`
                              : "—"
                            }
                          </td>
                          <td className="px-4 py-3">
                            <span className="bg-zinc-800 text-zinc-300 px-2 py-1 font-mono text-xs">
                              {cityBars.length} bares
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={async () => {
                                if (cityBars.length > 0) {
                                  toast.error("NO SE PUEDE ELIMINAR: HAY BARES EN ESTA CIUDAD");
                                  return;
                                }
                                if (!window.confirm(`¿Eliminar la ciudad "${city.name}"?`)) return;
                                try {
                                  await axios.delete(`${API}/cities/${city.city_id}`, { headers: getAuthHeaders() });
                                  setCities(cities.filter(c => c.city_id !== city.city_id));
                                  toast.success("CIUDAD ELIMINADA");
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || "ERROR AL ELIMINAR");
                                }
                              }}
                              className="text-zinc-500 hover:text-red-500" 
                              title="Eliminar"
                              disabled={cityBars.length > 0}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {cities.length === 0 && (
                <div className="p-8 text-center">
                  <MapPin className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">NO HAY CIUDADES REGISTRADAS</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* PLAN FEATURES TAB */}
          <TabsContent value="features">
            <div className="bg-[#111] border border-zinc-800 p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black text-white">CONFIGURACIÓN DE PLANES</h3>
                <Button 
                  onClick={handleSaveFeatures}
                  disabled={savingFeatures}
                  className="bg-primary hover:bg-red-700 text-white font-mono"
                >
                  {savingFeatures ? "GUARDANDO..." : "GUARDAR CAMBIOS"}
                </Button>
              </div>
              
              <p className="text-zinc-500 text-sm mb-6">
                Configura qué funcionalidades están disponibles para cada plan. Los cambios afectarán a todos los bares con ese plan.
              </p>

              {planFeatures && (
                <div className="grid md:grid-cols-2 gap-6">
                  {/* FREE PLAN */}
                  <div className="border border-zinc-700 p-6">
                    <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <Store className="w-5 h-5 text-zinc-500" />
                      PLAN GARAGE (GRATIS)
                    </h4>
                    <div className="space-y-4">
                      {Object.entries(planFeatures.free || {}).map(([feature, value]) => (
                        <div key={feature} className="flex items-center justify-between">
                          <span className="text-zinc-400 text-sm">
                            {featureLabels[feature] || feature}
                          </span>
                          {typeof value === "boolean" ? (
                            <Switch
                              checked={value}
                              onCheckedChange={(checked) => handleFeatureToggle("free", feature, checked)}
                              data-testid={`free-${feature}-toggle`}
                            />
                          ) : (
                            <Input
                              type="number"
                              value={value}
                              onChange={(e) => handleFeatureToggle("free", feature, parseInt(e.target.value) || 0)}
                              className="input-ruido w-20 text-center"
                              data-testid={`free-${feature}-input`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* PRO PLAN */}
                  <div className="border border-primary/50 bg-primary/5 p-6">
                    <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <Crown className="w-5 h-5 text-primary" />
                      PLAN BACKSTAGE (PRO) - 29€/mes
                    </h4>
                    <div className="space-y-4">
                      {Object.entries(planFeatures.pro || {}).map(([feature, value]) => (
                        <div key={feature} className="flex items-center justify-between">
                          <span className="text-zinc-400 text-sm">
                            {featureLabels[feature] || feature}
                          </span>
                          {typeof value === "boolean" ? (
                            <Switch
                              checked={value}
                              onCheckedChange={(checked) => handleFeatureToggle("pro", feature, checked)}
                              data-testid={`pro-${feature}-toggle`}
                            />
                          ) : (
                            <Input
                              type="number"
                              value={value}
                              onChange={(e) => handleFeatureToggle("pro", feature, parseInt(e.target.value) || -1)}
                              className="input-ruido w-20 text-center"
                              placeholder="-1 = ilimitado"
                              data-testid={`pro-${feature}-input`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-zinc-600 text-xs mt-4">* -1 = ilimitado</p>
                  </div>
                </div>
              )}

              <div className="mt-8 p-4 bg-zinc-900 border border-zinc-800">
                <h5 className="text-sm font-bold text-zinc-400 mb-2">LEYENDA DE FUNCIONALIDADES (BARES)</h5>
                <ul className="text-xs text-zinc-500 space-y-1">
                  <li><strong>Gestión de Calendario:</strong> Ver y gestionar el calendario de eventos</li>
                  <li><strong>Buscador de Bandas:</strong> Buscar y contactar bandas disponibles</li>
                  <li><strong>Crear Eventos:</strong> Publicar nuevos eventos</li>
                  <li><strong>Gestión de Disponibilidad:</strong> Marcar fechas disponibles para conciertos</li>
                  <li><strong>Recibir Solicitudes:</strong> Recibir propuestas de bandas</li>
                  <li><strong>Link de Entradas:</strong> Añadir enlace de venta de entradas a eventos</li>
                  <li><strong>Posicionamiento Destacado:</strong> Aparecer primero en búsquedas</li>
                </ul>
              </div>
            </div>

            {/* BAND PLANS SECTION */}
            <div className="bg-[#111] border border-zinc-800 p-6 mt-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black text-white flex items-center gap-2">
                  <Guitar className="w-6 h-6 text-primary" />
                  PLANES PARA BANDAS
                </h3>
                <Button 
                  onClick={handleSaveBandFeatures}
                  disabled={savingBandFeatures}
                  className="bg-primary hover:bg-red-700 text-white font-mono"
                >
                  {savingBandFeatures ? "GUARDANDO..." : "GUARDAR CAMBIOS"}
                </Button>
              </div>
              
              <p className="text-zinc-500 text-sm mb-6">
                Configura qué funcionalidades están disponibles para cada plan de banda.
              </p>

              {bandPlanFeatures && (
                <div className="grid md:grid-cols-2 gap-6">
                  {/* BAND FREE PLAN */}
                  <div className="border border-zinc-700 p-6">
                    <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <Guitar className="w-5 h-5 text-zinc-500" />
                      PLAN GARAGE (GRATIS)
                    </h4>
                    <p className="text-zinc-600 text-xs mb-4">Gratis para siempre</p>
                    <div className="space-y-4">
                      {Object.entries(bandPlanFeatures.free || {}).map(([feature, value]) => (
                        <div key={feature} className="flex items-center justify-between">
                          <span className="text-zinc-400 text-sm">
                            {bandFeatureLabels[feature] || feature}
                          </span>
                          <Switch
                            checked={value}
                            onCheckedChange={(checked) => handleBandFeatureToggle("free", feature, checked)}
                            data-testid={`band-free-${feature}-toggle`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* BAND PRO PLAN */}
                  <div className="border border-primary/50 bg-primary/5 p-6">
                    <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                      <Crown className="w-5 h-5 text-primary" />
                      PLAN STAGE (PRO) - 9€/mes
                    </h4>
                    <p className="text-green-500 text-xs mb-4">Gratis hasta 01/05/2026</p>
                    <div className="space-y-4">
                      {Object.entries(bandPlanFeatures.pro || {}).map(([feature, value]) => (
                        <div key={feature} className="flex items-center justify-between">
                          <span className="text-zinc-400 text-sm">
                            {bandFeatureLabels[feature] || feature}
                          </span>
                          <Switch
                            checked={value}
                            onCheckedChange={(checked) => handleBandFeatureToggle("pro", feature, checked)}
                            data-testid={`band-pro-${feature}-toggle`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-8 p-4 bg-zinc-900 border border-zinc-800">
                <h5 className="text-sm font-bold text-zinc-400 mb-2">LEYENDA DE FUNCIONALIDADES (BANDAS)</h5>
                <ul className="text-xs text-zinc-500 space-y-1">
                  <li><strong>Proponer Conciertos:</strong> Enviar propuestas de conciertos a bares</li>
                  <li><strong>Gestión de Calendario:</strong> Marcar fechas disponibles</li>
                  <li><strong>Recibir Solicitudes:</strong> Recibir propuestas de bares</li>
                  <li><strong>Buscador de Bares:</strong> Buscar bares disponibles</li>
                  <li><strong>Gestión de Perfil:</strong> Editar perfil de la banda</li>
                  <li><strong>Posicionamiento Destacado:</strong> Aparecer primero en búsquedas</li>
                </ul>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* CREATE BAR DIALOG - FULL FIELDS */}
        <Dialog open={createBarDialog} onOpenChange={setCreateBarDialog}>
          <DialogContent className="bg-[#111] border-zinc-800 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-black text-white">CREAR BAR</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">NOMBRE PROPIETARIO *</Label>
                  <Input value={newBar.name} onChange={(e) => setNewBar({...newBar, name: e.target.value})} className="input-ruido" placeholder="Juan García" />
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">EMAIL *</Label>
                  <Input type="email" value={newBar.email} onChange={(e) => setNewBar({...newBar, email: e.target.value})} className="input-ruido" placeholder="bar@email.com" />
                </div>
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">CONTRASEÑA *</Label>
                <Input type="password" value={newBar.password} onChange={(e) => setNewBar({...newBar, password: e.target.value})} className="input-ruido" />
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">NOMBRE DEL BAR *</Label>
                <Input value={newBar.bar_name} onChange={(e) => setNewBar({...newBar, bar_name: e.target.value})} className="input-ruido" placeholder="Rock Bar" />
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">DESCRIPCIÓN</Label>
                <Textarea value={newBar.description} onChange={(e) => setNewBar({...newBar, description: e.target.value})} className="input-ruido min-h-[80px]" placeholder="Descripción del bar..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">CIUDAD</Label>
                  <Select value={newBar.city_id} onValueChange={(v) => setNewBar({...newBar, city_id: v})}>
                    <SelectTrigger className="input-ruido"><SelectValue placeholder="Seleccionar ciudad" /></SelectTrigger>
                    <SelectContent className="bg-[#111] border-zinc-800 rounded-none max-h-60">
                      {cities.map(c => (<SelectItem key={c.city_id} value={c.city_id} className="font-mono text-sm">{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2 mt-2">
                    <Input 
                      value={newCityName} 
                      onChange={(e) => setNewCityName(e.target.value)} 
                      className="input-ruido text-sm" 
                      placeholder="Nueva ciudad..."
                    />
                    <Button 
                      type="button"
                      onClick={handleCreateCity} 
                      disabled={creatingCity}
                      size="sm"
                      className="bg-zinc-700 hover:bg-zinc-600 text-white font-mono text-xs"
                    >
                      {creatingCity ? "..." : "+"}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Phone className="inline w-3 h-3 mr-1" />TELÉFONO</Label>
                  <Input value={newBar.phone} onChange={(e) => setNewBar({...newBar, phone: e.target.value})} className="input-ruido" placeholder="+34 600 000 000" />
                </div>
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block"><MapPin className="inline w-3 h-3 mr-1" />DIRECCIÓN</Label>
                <Input value={newBar.address} onChange={(e) => setNewBar({...newBar, address: e.target.value})} className="input-ruido" placeholder="C/ Ejemplo 123, Barcelona" />
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Globe className="inline w-3 h-3 mr-1" />WEB</Label>
                <Input value={newBar.website} onChange={(e) => setNewBar({...newBar, website: e.target.value})} className="input-ruido" placeholder="www.mibar.com" />
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Mail className="inline w-3 h-3 mr-1" />CONTACTO (link o texto)</Label>
                <Input value={newBar.contact} onChange={(e) => setNewBar({...newBar, contact: e.target.value})} className="input-ruido" placeholder="email, Instagram, formulario..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">LATITUD</Label>
                  <Input type="number" step="any" value={newBar.latitude} onChange={(e) => setNewBar({...newBar, latitude: e.target.value})} className="input-ruido" placeholder="41.3851" />
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">LONGITUD</Label>
                  <Input type="number" step="any" value={newBar.longitude} onChange={(e) => setNewBar({...newBar, longitude: e.target.value})} className="input-ruido" placeholder="2.1734" />
                </div>
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">IMAGEN</Label>
                <ImageUploadWithPosition 
                  value={newBar.image_url} 
                  onChange={(url) => setNewBar(prev => ({...prev, image_url: url}))} 
                  position={newBar.image_position} 
                  onPositionChange={(pos) => setNewBar(prev => ({...prev, image_position: pos}))} 
                  token={token} 
                />
              </div>
              <Button onClick={handleCreateBar} className="w-full btn-ruido bg-primary text-white font-mono">CREAR BAR</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* EDIT BAR DIALOG - FULL FIELDS */}
        <Dialog open={editBarDialog} onOpenChange={setEditBarDialog}>
          <DialogContent className="bg-[#111] border-zinc-800 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-black text-white">EDITAR BAR</DialogTitle>
            </DialogHeader>
            {selectedBar && (
              <div className="space-y-4">
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">NOMBRE DEL BAR</Label>
                  <Input value={selectedBar.name || ""} onChange={(e) => setSelectedBar({...selectedBar, name: e.target.value})} className="input-ruido" />
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">DESCRIPCIÓN</Label>
                  <Textarea value={selectedBar.description || ""} onChange={(e) => setSelectedBar({...selectedBar, description: e.target.value})} className="input-ruido min-h-[80px]" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block">CIUDAD</Label>
                    <Select value={selectedBar.city_id || ""} onValueChange={(v) => setSelectedBar({...selectedBar, city_id: v})}>
                      <SelectTrigger className="input-ruido"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent className="bg-[#111] border-zinc-800 rounded-none">
                        {cities.map(c => (<SelectItem key={c.city_id} value={c.city_id} className="font-mono text-sm">{c.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block">TIPO DE LOCAL</Label>
                    <Select value={selectedBar.venue_type || "none"} onValueChange={(v) => setSelectedBar({...selectedBar, venue_type: v === "none" ? null : v})}>
                      <SelectTrigger className="input-ruido"><SelectValue placeholder="Seleccionar tipo" /></SelectTrigger>
                      <SelectContent className="bg-[#111] border-zinc-800 rounded-none">
                        <SelectItem value="none" className="font-mono text-sm">SIN ESPECIFICAR</SelectItem>
                        {VENUE_TYPES.map(t => (<SelectItem key={t.id} value={t.id} className="font-mono text-sm">{t.label.toUpperCase()}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Phone className="inline w-3 h-3 mr-1" />TELÉFONO</Label>
                    <Input value={selectedBar.phone || ""} onChange={(e) => setSelectedBar({...selectedBar, phone: e.target.value})} className="input-ruido" />
                  </div>
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block"><MapPin className="inline w-3 h-3 mr-1" />DIRECCIÓN</Label>
                    <Input value={selectedBar.address || ""} onChange={(e) => setSelectedBar({...selectedBar, address: e.target.value})} className="input-ruido" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Mail className="inline w-3 h-3 mr-1" />EMAIL</Label>
                    <Input value={selectedBar.email || ""} onChange={(e) => setSelectedBar({...selectedBar, email: e.target.value})} className="input-ruido" />
                  </div>
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Globe className="inline w-3 h-3 mr-1" />WEB</Label>
                    <Input value={selectedBar.website || ""} onChange={(e) => setSelectedBar({...selectedBar, website: e.target.value})} className="input-ruido" />
                  </div>
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">CONTACTO (link o texto)</Label>
                  <Input value={selectedBar.contact || ""} onChange={(e) => setSelectedBar({...selectedBar, contact: e.target.value})} className="input-ruido" placeholder="email, Instagram, formulario..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block">LATITUD</Label>
                    <Input type="number" step="any" value={selectedBar.latitude || ""} onChange={(e) => setSelectedBar({...selectedBar, latitude: parseFloat(e.target.value) || null})} className="input-ruido" />
                  </div>
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block">LONGITUD</Label>
                    <Input type="number" step="any" value={selectedBar.longitude || ""} onChange={(e) => setSelectedBar({...selectedBar, longitude: parseFloat(e.target.value) || null})} className="input-ruido" />
                  </div>
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">IMAGEN</Label>
                  <ImageUploadWithPosition 
                    value={selectedBar.image_url} 
                    onChange={(url) => setSelectedBar(prev => ({...prev, image_url: url}))} 
                    position={selectedBar.image_position || 50} 
                    onPositionChange={(pos) => setSelectedBar(prev => ({...prev, image_position: pos}))} 
                    token={token} 
                  />
                </div>
                
                {/* Visibility Toggles */}
                <div className="border-t border-zinc-800 pt-4 mt-4">
                  <Label className="font-mono text-xs text-zinc-500 mb-3 block flex items-center gap-2">
                    <Eye className="w-3 h-3" />
                    VISIBILIDAD DE CONTACTO
                  </Label>
                  <div className="space-y-3 bg-[#0a0a0a] p-3 border border-zinc-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Phone className="w-3 h-3 text-zinc-500" />
                        <span className="text-sm text-white">Teléfono</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-zinc-500">{selectedBar.show_phone !== false ? "VISIBLE" : "OCULTO"}</span>
                        <Switch checked={selectedBar.show_phone !== false} onCheckedChange={(checked) => setSelectedBar({...selectedBar, show_phone: checked})} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Mail className="w-3 h-3 text-zinc-500" />
                        <span className="text-sm text-white">Email</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-zinc-500">{selectedBar.show_email !== false ? "VISIBLE" : "OCULTO"}</span>
                        <Switch checked={selectedBar.show_email !== false} onCheckedChange={(checked) => setSelectedBar({...selectedBar, show_email: checked})} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Globe className="w-3 h-3 text-zinc-500" />
                        <span className="text-sm text-white">Contacto adicional</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-zinc-500">{selectedBar.show_contact !== false ? "VISIBLE" : "OCULTO"}</span>
                        <Switch checked={selectedBar.show_contact !== false} onCheckedChange={(checked) => setSelectedBar({...selectedBar, show_contact: checked})} />
                      </div>
                    </div>
                  </div>
                </div>
                
                <Button onClick={handleSaveBar} className="w-full btn-ruido bg-primary text-white font-mono">GUARDAR</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* CREATE EVENT DIALOG - FULL FIELDS */}
        <Dialog open={createEventDialog} onOpenChange={setCreateEventDialog}>
          <DialogContent className="bg-[#111] border-zinc-800 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-black text-white">CREAR EVENTO</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">BAR *</Label>
                <Select value={newEvent.bar_id} onValueChange={(v) => setNewEvent({...newEvent, bar_id: v})}>
                  <SelectTrigger className="input-ruido"><SelectValue placeholder="Seleccionar bar" /></SelectTrigger>
                  <SelectContent className="bg-[#111] border-zinc-800 rounded-none">
                    {bars.map(b => (<SelectItem key={b.bar_id} value={b.bar_id} className="font-mono text-sm">{b.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">TÍTULO *</Label>
                <Input value={newEvent.title} onChange={(e) => setNewEvent({...newEvent, title: e.target.value})} className="input-ruido" placeholder="Concierto de Rock" />
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">DESCRIPCIÓN</Label>
                <Textarea value={newEvent.description} onChange={(e) => setNewEvent({...newEvent, description: e.target.value})} className="input-ruido min-h-[80px]" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">FECHA *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full input-ruido justify-start">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {newEvent.event_date ? format(newEvent.event_date, "d MMM", { locale: es }) : "Fecha"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#111] border-zinc-800 rounded-none">
                      <Calendar mode="single" selected={newEvent.event_date} onSelect={(d) => setNewEvent({...newEvent, event_date: d})} locale={es} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Clock className="inline w-3 h-3 mr-1" />HORA INICIO</Label>
                  <Input type="time" value={newEvent.event_time} onChange={(e) => setNewEvent({...newEvent, event_time: e.target.value})} className="input-ruido" />
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Clock className="inline w-3 h-3 mr-1" />HORA FIN</Label>
                  <Input type="time" value={newEvent.end_time} onChange={(e) => setNewEvent({...newEvent, end_time: e.target.value})} className="input-ruido" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block"><DollarSign className="inline w-3 h-3 mr-1" />PRECIO</Label>
                  <Input value={newEvent.price} onChange={(e) => setNewEvent({...newEvent, price: e.target.value})} className="input-ruido" placeholder="10€ / Gratis" />
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Ticket className="inline w-3 h-3 mr-1" />URL ENTRADAS</Label>
                  <Input value={newEvent.ticket_url} onChange={(e) => setNewEvent({...newEvent, ticket_url: e.target.value})} className="input-ruido" placeholder="https://..." />
                </div>
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">INFO ENTRADAS (texto adicional)</Label>
                <Input value={newEvent.ticket_info} onChange={(e) => setNewEvent({...newEvent, ticket_info: e.target.value})} className="input-ruido" placeholder="Ej: Venta en taquilla desde 18h" />
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Tag className="inline w-3 h-3 mr-1" />ETIQUETAS</Label>
                <div className="flex flex-wrap gap-2">
                  {EVENT_TAGS.map(tag => (
                    <button key={tag} type="button" onClick={() => { const tags = newEvent.tags || []; setNewEvent({...newEvent, tags: tags.includes(tag) ? tags.filter(t => t !== tag) : [...tags, tag]}); }}
                      className={`px-3 py-1 font-mono text-xs uppercase border ${(newEvent.tags || []).includes(tag) ? "bg-primary text-white border-primary" : "border-zinc-700 text-zinc-400 hover:border-primary"}`}
                    >{tag}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Guitar className="inline w-3 h-3 mr-1" />GÉNEROS MUSICALES</Label>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                  {MUSIC_GENRES.map(genre => (
                    <button key={genre} type="button" onClick={() => { const genres = newEvent.music_genres || []; setNewEvent({...newEvent, music_genres: genres.includes(genre) ? genres.filter(g => g !== genre) : [...genres, genre]}); }}
                      className={`px-2 py-1 font-mono text-xs border ${(newEvent.music_genres || []).includes(genre) ? "bg-zinc-700 text-white border-zinc-600" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}
                    >{genre}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">IMAGEN</Label>
                <ImageUploadWithPosition 
                  value={newEvent.image_url} 
                  onChange={(url) => setNewEvent(prev => ({...prev, image_url: url}))} 
                  position={newEvent.image_position} 
                  onPositionChange={(pos) => setNewEvent(prev => ({...prev, image_position: pos}))} 
                  token={token} 
                />
              </div>
              <Button onClick={handleCreateEvent} className="w-full btn-ruido bg-primary text-white font-mono">CREAR EVENTO</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* EDIT EVENT DIALOG - FULL FIELDS */}
        <Dialog open={editEventDialog} onOpenChange={setEditEventDialog}>
          <DialogContent className="bg-[#111] border-zinc-800 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-black text-white">EDITAR EVENTO</DialogTitle>
            </DialogHeader>
            {selectedEvent && (
              <div className="space-y-4">
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">TÍTULO</Label>
                  <Input value={selectedEvent.title || ""} onChange={(e) => setSelectedEvent({...selectedEvent, title: e.target.value})} className="input-ruido" />
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">DESCRIPCIÓN</Label>
                  <Textarea value={selectedEvent.description || ""} onChange={(e) => setSelectedEvent({...selectedEvent, description: e.target.value})} className="input-ruido min-h-[80px]" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block">FECHA</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full input-ruido justify-start">
                          <CalendarIcon className="w-4 h-4 mr-2" />
                          {selectedEvent.event_date ? format(new Date(selectedEvent.event_date), "d MMM", { locale: es }) : "Fecha"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#111] border-zinc-800 rounded-none">
                        <Calendar mode="single" selected={selectedEvent.event_date ? new Date(selectedEvent.event_date) : null} onSelect={(d) => setSelectedEvent({...selectedEvent, event_date: d})} locale={es} />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Clock className="inline w-3 h-3 mr-1" />HORA INICIO</Label>
                    <Input type="time" value={selectedEvent.event_time || "20:00"} onChange={(e) => setSelectedEvent({...selectedEvent, event_time: e.target.value})} className="input-ruido" />
                  </div>
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Clock className="inline w-3 h-3 mr-1" />HORA FIN</Label>
                    <Input type="time" value={selectedEvent.end_time || ""} onChange={(e) => setSelectedEvent({...selectedEvent, end_time: e.target.value})} className="input-ruido" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block"><DollarSign className="inline w-3 h-3 mr-1" />PRECIO</Label>
                    <Input value={selectedEvent.price || ""} onChange={(e) => setSelectedEvent({...selectedEvent, price: e.target.value})} className="input-ruido" />
                  </div>
                  <div>
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Ticket className="inline w-3 h-3 mr-1" />URL ENTRADAS</Label>
                    <Input value={selectedEvent.ticket_url || ""} onChange={(e) => setSelectedEvent({...selectedEvent, ticket_url: e.target.value})} className="input-ruido" />
                  </div>
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">INFO ENTRADAS (texto adicional)</Label>
                  <Input value={selectedEvent.ticket_info || ""} onChange={(e) => setSelectedEvent({...selectedEvent, ticket_info: e.target.value})} className="input-ruido" placeholder="Ej: Venta en taquilla desde 18h" />
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Tag className="inline w-3 h-3 mr-1" />ETIQUETAS</Label>
                  <div className="flex flex-wrap gap-2">
                    {EVENT_TAGS.map(tag => (
                      <button key={tag} type="button" onClick={() => { const tags = selectedEvent.tags || []; setSelectedEvent({...selectedEvent, tags: tags.includes(tag) ? tags.filter(t => t !== tag) : [...tags, tag]}); }}
                        className={`px-3 py-1 font-mono text-xs uppercase border ${(selectedEvent.tags || []).includes(tag) ? "bg-primary text-white border-primary" : "border-zinc-700 text-zinc-400 hover:border-primary"}`}
                      >{tag}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block"><Guitar className="inline w-3 h-3 mr-1" />GÉNEROS MUSICALES</Label>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                    {MUSIC_GENRES.map(genre => (
                      <button key={genre} type="button" onClick={() => { const genres = selectedEvent.music_genres || []; setSelectedEvent({...selectedEvent, music_genres: genres.includes(genre) ? genres.filter(g => g !== genre) : [...genres, genre]}); }}
                        className={`px-2 py-1 font-mono text-xs border ${(selectedEvent.music_genres || []).includes(genre) ? "bg-zinc-700 text-white border-zinc-600" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}
                      >{genre}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">IMAGEN</Label>
                  <ImageUploadWithPosition 
                    value={selectedEvent.image_url} 
                    onChange={(url) => setSelectedEvent(prev => ({...prev, image_url: url}))} 
                    position={selectedEvent.image_position || 50} 
                    onPositionChange={(pos) => setSelectedEvent(prev => ({...prev, image_position: pos}))} 
                    token={token} 
                  />
                </div>
                <Button onClick={handleSaveEvent} className="w-full btn-ruido bg-primary text-white font-mono">GUARDAR</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* PASSWORD DIALOG */}
        <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
          <DialogContent className="bg-[#111] border-zinc-800 rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-black text-white">CAMBIAR CONTRASEÑA</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">CONTRASEÑA ACTUAL</Label>
                <Input type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})} className="input-ruido" />
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">NUEVA CONTRASEÑA</Label>
                <Input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})} className="input-ruido" />
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">CONFIRMAR CONTRASEÑA</Label>
                <Input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})} className="input-ruido" />
              </div>
              <Button onClick={handleChangePassword} className="w-full btn-ruido bg-primary text-white font-mono">CAMBIAR</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* STRIPE CONFIG DIALOG */}
        <Dialog open={stripeDialog} onOpenChange={setStripeDialog}>
          <DialogContent className="bg-[#111] border-zinc-800 rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-black text-white">CONFIGURACIÓN STRIPE</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {stripeConfig && (
                <div className="bg-zinc-900 p-4 border border-zinc-800">
                  <p className="font-mono text-xs text-zinc-500 mb-1">ESTADO ACTUAL</p>
                  <p className={`font-mono text-sm ${stripeConfig.configured ? "text-green-400" : "text-yellow-400"}`}>
                    {stripeConfig.configured ? `Configurado (${stripeConfig.key_preview})` : "No configurado"}
                  </p>
                </div>
              )}
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">API KEY SECRETA DE STRIPE</Label>
                <div className="relative">
                  <Input type={showStripeKey ? "text" : "password"} value={stripeKey} onChange={(e) => setStripeKey(e.target.value)} className="input-ruido pr-10" placeholder="sk_live_..." />
                  <button type="button" onClick={() => setShowStripeKey(!showStripeKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
                    {showStripeKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="font-mono text-xs text-zinc-600 mt-2">Obtén tu API key en dashboard.stripe.com/apikeys</p>
              </div>
              <Button onClick={handleSaveStripeConfig} className="w-full btn-ruido bg-primary text-white font-mono">GUARDAR</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* ASSIGN USER TO BAR DIALOG */}
        <Dialog open={assignUserDialog} onOpenChange={setAssignUserDialog}>
          <DialogContent className="bg-[#111] border-zinc-800 rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-black text-white">ASIGNAR USUARIO AL BAR</DialogTitle>
            </DialogHeader>
            {selectedBar && (
              <div className="space-y-4">
                <div className="bg-zinc-900 p-4 border border-zinc-800">
                  <p className="font-mono text-xs text-zinc-500 mb-1">BAR SELECCIONADO</p>
                  <p className="text-white font-bold">{selectedBar.name}</p>
                  {selectedBar.owner_name && (
                    <p className="text-zinc-400 text-sm mt-1">Propietario actual: {selectedBar.owner_name}</p>
                  )}
                </div>
                
                {availableBarUsers.length === 0 ? (
                  <div className="text-center py-6">
                    <Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <p className="text-zinc-500 font-mono text-sm">NO HAY USUARIOS DISPONIBLES</p>
                    <p className="text-zinc-600 text-xs mt-2">Crea un usuario de bar primero</p>
                    <Button 
                      onClick={() => { setAssignUserDialog(false); setCreateUserDialog(true); }}
                      className="mt-4 btn-ruido bg-primary text-white font-mono text-xs"
                    >
                      + CREAR USUARIO BAR
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label className="font-mono text-xs text-zinc-500 mb-2 block">USUARIOS DISPONIBLES</Label>
                    {availableBarUsers.map(u => (
                      <div key={u.user_id} className="flex items-center justify-between bg-zinc-900 p-3 border border-zinc-800 hover:border-primary transition-colors">
                        <div>
                          <p className="text-white font-medium">{u.name}</p>
                          <p className="font-mono text-xs text-zinc-500">{u.email}</p>
                        </div>
                        <Button 
                          onClick={() => handleAssignUser(u.user_id)} 
                          className="btn-ruido bg-primary text-white font-mono text-xs"
                        >
                          ASIGNAR
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* EDIT USER DIALOG */}
        <Dialog open={editUserDialog} onOpenChange={setEditUserDialog}>
          <DialogContent className="bg-[#111] border-zinc-800 rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-black text-white">EDITAR USUARIO</DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4">
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">NOMBRE</Label>
                  <Input 
                    value={selectedUser.name || ""} 
                    onChange={(e) => setSelectedUser({...selectedUser, name: e.target.value})} 
                    className="input-ruido" 
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">EMAIL</Label>
                  <Input 
                    type="email"
                    value={selectedUser.email || ""} 
                    onChange={(e) => setSelectedUser({...selectedUser, email: e.target.value})} 
                    className="input-ruido" 
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">TIPO</Label>
                  <Select 
                    value={selectedUser.user_type || "user"} 
                    onValueChange={(v) => setSelectedUser({...selectedUser, user_type: v})}
                    disabled={selectedUser.user_type === "admin"}
                  >
                    <SelectTrigger className="input-ruido">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-zinc-800 rounded-none">
                      <SelectItem value="user" className="font-mono text-sm">USUARIO</SelectItem>
                      <SelectItem value="bar" className="font-mono text-sm">BAR</SelectItem>
                      <SelectItem value="partner" className="font-mono text-sm">PARTNER</SelectItem>
                      <SelectItem value="admin" className="font-mono text-sm" disabled>ADMIN</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">NUEVA CONTRASEÑA (opcional)</Label>
                  <Input 
                    type="password"
                    value={selectedUser.password || ""} 
                    onChange={(e) => setSelectedUser({...selectedUser, password: e.target.value})} 
                    className="input-ruido" 
                    placeholder="Dejar vacío para mantener"
                  />
                </div>
                <Button onClick={handleSaveUser} className="w-full btn-ruido bg-primary text-white font-mono">GUARDAR</Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* CREATE USER DIALOG */}
        <Dialog open={createUserDialog} onOpenChange={setCreateUserDialog}>
          <DialogContent className="bg-[#111] border-zinc-800 rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-black text-white">CREAR USUARIO</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">TIPO DE USUARIO *</Label>
                <Select 
                  value={newUser.user_type} 
                  onValueChange={(v) => setNewUser({...newUser, user_type: v})}
                >
                  <SelectTrigger className="input-ruido">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-zinc-800 rounded-none">
                    <SelectItem value="bar" className="font-mono text-sm">BAR</SelectItem>
                    <SelectItem value="partner" className="font-mono text-sm">PARTNER</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-zinc-500 text-xs mt-1">
                  {newUser.user_type === "partner" 
                    ? "Los Partners pueden crear eventos para cualquier bar" 
                    : "Los usuarios Bar se asignan a un bar existente"}
                </p>
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">NOMBRE *</Label>
                <Input 
                  value={newUser.name} 
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})} 
                  className="input-ruido" 
                  placeholder="Nombre del usuario"
                />
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">EMAIL *</Label>
                <Input 
                  type="email"
                  value={newUser.email} 
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})} 
                  className="input-ruido" 
                  placeholder="email@ejemplo.com"
                />
              </div>
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">CONTRASEÑA *</Label>
                <Input 
                  type="password"
                  value={newUser.password} 
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})} 
                  className="input-ruido" 
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
              <Button onClick={handleCreateUser} className="w-full btn-ruido bg-primary text-white font-mono">CREAR USUARIO</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* BULK EDIT BARS DIALOG */}
        <Dialog open={bulkEditDialog} onOpenChange={setBulkEditDialog}>
          <DialogContent className="bg-[#111] border-zinc-800 rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-black text-white">
                EDITAR {selectedBarIds.length} BARES
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-zinc-400 text-sm">
                Los cambios se aplicarán a todos los bares seleccionados. Deja vacío lo que no quieras cambiar.
              </p>
              
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">TIPO DE LOCAL</Label>
                <Select value={bulkVenueType || "none"} onValueChange={(v) => setBulkVenueType(v === "none" ? "" : v)}>
                  <SelectTrigger className="input-ruido">
                    <SelectValue placeholder="Sin cambiar" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-zinc-800 rounded-none">
                    <SelectItem value="none" className="font-mono text-sm">SIN CAMBIAR</SelectItem>
                    {VENUE_TYPES.map(t => (
                      <SelectItem key={t.id} value={t.id} className="font-mono text-sm">
                        {t.label.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">IMAGEN (URL)</Label>
                <Input 
                  value={bulkImageUrl} 
                  onChange={(e) => setBulkImageUrl(e.target.value)} 
                  className="input-ruido" 
                  placeholder="https://... (dejar vacío para no cambiar)"
                />
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setBulkEditDialog(false);
                    setBulkVenueType("");
                    setBulkImageUrl("");
                  }} 
                  className="flex-1 border-zinc-700 font-mono"
                >
                  CANCELAR
                </Button>
                <Button 
                  onClick={handleBulkUpdate} 
                  className="flex-1 btn-ruido bg-primary text-white font-mono"
                >
                  APLICAR CAMBIOS
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
